import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Flex, SegmentedControl, Text } from 'core/components';
import UdeDataViewWrapper from 'app/components/dataviews/ude/UdeDataViewWrapper';
import { useChartSync } from 'app/views/boards/useChartSync';
import { buildAgentMetricCharts } from './buildAgentMetricQueries';

interface AgentUdeMetricsViewProps {
  /** The test model */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  test: any;
  /** The source agent ID */
  agent_id: string | number;
  /** The target (IP, hostname, or agent ID) */
  target?: string;
  /** Start epoch seconds */
  startDate: number;
  /** End epoch seconds */
  endDate: number;
}

const CHART_OVERRIDES = {
  yAxis: { title: { text: '' } }
};

const LEGEND_POSITION_OPTIONS = [
  { label: 'Bottom', value: 'bottom' },
  { label: 'Right', value: 'right' }
];

const CHART_SIZE_OPTIONS = [
  { label: 'Normal', value: 'normal' },
  { label: 'Compact', value: 'compact' }
];

const AgentUdeMetricsView = observer(({ test, agent_id, target, startDate, endDate }: AgentUdeMetricsViewProps) => {
  const registerChart = useChartSync({ syncTooltips: true });
  const [legendPosition, setLegendPosition] = useState<'bottom' | 'right'>('right');
  const [chartSize, setChartSize] = useState<'normal' | 'compact'>('normal');

  const chartSettings = useMemo(
    () => ({ chart: { legend: { position: legendPosition, showRollups: true } } }),
    [legendPosition]
  );

  const chartHeight = chartSize === 'compact' ? 200 : 400;

  const charts = useMemo(
    () => buildAgentMetricCharts(test, agent_id, target, { start: startDate, end: endDate }),
    [test, agent_id, target, startDate, endDate]
  );

  return (
    <Flex flexDirection="column" gap={3}>
      <Flex alignItems="flex-end" flexWrap="wrap" gap={2}>
        <Box>
          <Text as="div" small muted mb="4px" fontWeight="bold">
            Legend
          </Text>
          <SegmentedControl
            options={LEGEND_POSITION_OPTIONS}
            value={legendPosition}
            onChange={setLegendPosition as (v: string) => void}
          />
        </Box>
        <Box>
          <Text as="div" small muted mb="4px" fontWeight="bold">
            Chart Size
          </Text>
          <SegmentedControl
            options={CHART_SIZE_OPTIONS}
            value={chartSize}
            onChange={setChartSize as (v: string) => void}
          />
        </Box>
      </Flex>
      {charts.map(({ key, title, query }) => (
        <Box key={key} height={chartHeight}>
          <UdeDataViewWrapper
            query={query}
            title={title}
            showOpenInUde
            onChartRef={registerChart(key)}
            chartOverrides={CHART_OVERRIDES}
            settings={chartSettings}
          />
        </Box>
      ))}
    </Flex>
  );
});

export default AgentUdeMetricsView;
